/**
 * Block 1 Practice scenarios catalog · B1-FOUND-NN.
 * Per Wave 5 ROLE doc · Block 1 Atelier integration · 5 B1-FOUND scenarios total.
 *
 * Numbering scheme:
 *   B1-FOUND-NN — Block 1 fundamentals (S01-S05 anchor map)
 *
 * Voice continuity: ADHD-novice register · конец 1-го месяца обучения.
 * Sample answers = junior-grade exemplars · NOT senior frameworks без teach-block.
 *
 * W5.2 authoring batch:
 *   - batch 1 · B1-FOUND-01 (Sign Up form review) · midpoint review
 *   - batch 2 · B1-FOUND-02..05 после ack
 *
 * Sprint anchor map:
 *   B1-FOUND-01 → S02 T07 (Acceptance Criteria deep)
 *   B1-FOUND-02 → S03/S04 (Test Types · TC Writing)
 *   B1-FOUND-03 → S03/S04 (Bug Reports · synergy с planted bug library)
 *   B1-FOUND-04 → S05 T11 (Test Design Techniques)
 *   B1-FOUND-05 → S04 T11 (Full Feature Documentation · Block 1 capstone-like)
 */

import type { Scenario } from '@/types';

// ────────────────────────────────────────────────────────────────────────────
// Sprint 02 · Acceptance Criteria · B1-FOUND-01
// ────────────────────────────────────────────────────────────────────────────

const B1_FOUND_01: Scenario = {
  id: 'B1-FOUND-01',
  sprint: 2,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/sign-up',
  mountStrategy: 'iframe-route',
  title: 'Sign Up form review · напиши Acceptance Criteria по 4 категориям',
  brief:
    'Серж: «Глянь Sign Up modal · напиши User Story + 6-8 Acceptance Criteria · покрой 4 категории: happy path · negative · edge · security. AC patterns ты уже видел в Block 1 practice — теперь формализуй systematically». Маша: «Pre-release sign-off doc · команда читает перед merge».',
  taskMd: [
    '**Контекст.** Atelier ships Sign Up modal · перед release нужны formalized Acceptance Criteria. AC = граница между «works in dev» и «works in prod» · без неё dev и QA пишут разное · бесконечные circular discussions.',
    '',
    '**Что делать.**',
    '1. Открой `/sign-up` в Atelier sandbox · познакомься с формой (email · username · password · terms checkbox · submit). Поиграй с разными inputs · посмотри что валидируется и что нет.',
    '2. В правой panel · editor с template:',
    '   - **User Story** (As a · I want · So that)',
    '   - **Acceptance Criteria** (6-8 items · grouped by 4 categories):',
    '     - Happy path (1-2 AC · валидная регистрация · success state)',
    '     - Negative (2-3 AC · invalid email · weak password · duplicate username)',
    '     - Edge (1-2 AC · boundary lengths · special chars · unicode)',
    '     - Security (1-2 AC · password masked · no SQL injection · session token)',
    '3. Каждый AC — формат `Given · When · Then`.',
    '4. Жми **Save** · auto-grading проверит coverage по категориям (≥1 AC из каждой) · сдавай.',
    '',
    '**Что важно.**',
    '',
    '- **4 категории coverage** · happy path только = optimistic · negative + edge + security ловят 80% bugs которые иначе вылезут на проде',
    '- **INVEST** (Independent · Negotiable · Valuable · Estimable · Small · Testable) — чек-лист «good AC». Применяй к каждому пункту',
    '- **Given-When-Then** · structured · auto-checkable · переносится в BDD/Cucumber если команда туда придёт',
    '',
    'Synergy с planted bugs · если sandbox-mode активирован · увидишь поведения которые помогут шортлистить negative/edge AC. Email plain text проходит submit (SANDBOX-BUG-001) → добавь AC «email matches /^[^@]+@[^@]+\\.[^@]+$/». Password 1-символьный проходит (SANDBOX-BUG-003) → security AC «≥8 chars + ≥1 digit». Pedagogical loop «наблюдай → формализуй» работает в обе стороны: AC drives тестирование · тестирование surfaces missing AC.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: 'User Story в формате «As a / I want / So that»', weight: 1 },
      { id: 'r2', text: '≥1 AC категории Happy path · happy submit success behavior', weight: 1 },
      { id: 'r3', text: '≥2 AC категории Negative · invalid email / weak password / duplicate', weight: 2 },
      { id: 'r4', text: '≥1 AC категории Edge · boundary length / special chars / unicode', weight: 1 },
      { id: 'r5', text: '≥1 AC категории Security · password masked / no injection / session token', weight: 2 },
      { id: 'r6', text: 'Каждый AC в формате Given-When-Then (или эквивалентный structured pattern)', weight: 1 },
      { id: 'r7', text: 'Total AC count в range 6-8 · NOT 3 · NOT 15 · balanced', weight: 1 },
      { id: 'r8', text: 'AC concrete · measurable · auto-checkable (not vague «works correctly»)', weight: 1 },
    ],
    passThreshold: 7,
  },
  sampleAnswer: [
    '## User Story',
    '',
    'As a new user · I want к register an account · So that I can buy artworks and leave reviews.',
    '',
    '## Acceptance Criteria',
    '',
    '### Happy path',
    '',
    '**AC-01** · Successful registration shows success state',
    '- **Given** I am on /sign-up page · valid email "anya@art.studio" · valid username "anya_petrova" · valid password "MyPassW0rd!" · terms checkbox checked',
    '- **When** I click "Зарегистрироваться"',
    '- **Then** registration succeeds · success state «Регистрация прошла» visible · my username displayed',
    '',
    '### Negative',
    '',
    '**AC-02** · Invalid email format shows specific error',
    '- **Given** I am on /sign-up · username/password/terms всё валидное · email = "notanemail" (без @)',
    '- **When** I click submit',
    '- **Then** error appears «Введите корректный email · формат: name@domain.com» · form NOT submitted · email field highlighted',
    '',
    '**AC-03** · Username too short shows specific error',
    '- **Given** I am on /sign-up · email/password/terms всё валидное · username = "ab" (2 chars)',
    '- **When** I click submit',
    '- **Then** error appears «Username должен быть минимум 3 символа» · NOT generic «Something went wrong»',
    '',
    '**AC-04** · Terms checkbox unchecked blocks submit',
    '- **Given** I have valid email/username/password · terms checkbox UNCHECKED',
    '- **When** I click submit',
    '- **Then** error appears «Согласитесь с условиями использования» · form NOT submitted',
    '',
    '### Edge',
    '',
    '**AC-05** · Username с unicode chars accepted',
    '- **Given** I enter username "анна_2026" (Cyrillic + digits + underscore · 9 chars)',
    '- **When** rest of form valid · I click submit',
    '- **Then** registration succeeds (unicode usernames supported · правило: ≥3 chars total · whatever the script)',
    '',
    '### Security',
    '',
    '**AC-06** · Password input masked by default',
    '- **Given** I am on /sign-up',
    '- **When** I focus password field · type characters',
    '- **Then** characters render as bullets/dots (not plain text) · matches `<input type="password">` behavior',
    '',
    '**AC-07** · Password ≥8 chars enforced',
    '- **Given** I enter password "abc" (3 chars)',
    '- **When** rest of form valid · I click submit',
    '- **Then** error appears «Пароль должен быть минимум 8 символов» · form NOT submitted · short password rejected (security · no brute-force-trivial accounts)',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант** добавляет 4 AC категории beyond Block 1 baseline:',
    '',
    '1. **Performance AC** · «registration submit completes <2s p95 · loading state shown after 500ms»',
    '2. **Accessibility AC** · «keyboard-only navigation · Esc closes modal · all fields tab-reachable · ARIA labels» (WCAG 2.1 AA)',
    '3. **i18n AC** · «error messages localized к user locale · placeholders translated»',
    '4. **Resilience AC** · «registration retries on transient network failure · idempotent · no duplicate accounts»',
    '',
    'Block 1 = 4 categories baseline (happy/negative/edge/security). Block 3-4 затронет остальные через perf/a11y/i18n/network scenarios.',
  ].join('\n'),
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-02-required',
    'documentation',
  ],
  estimateMin: 35,
  skills: { 'Requirements Analysis': 5, 'Test Documentation': 4 },
  requires: ['msw-api'],
};

// ────────────────────────────────────────────────────────────────────────────
// Sprint 03 · Test Cases · B1-FOUND-02
// ────────────────────────────────────────────────────────────────────────────

const B1_FOUND_02: Scenario = {
  id: 'B1-FOUND-02',
  sprint: 3,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/cart',
  mountStrategy: 'iframe-route',
  title: 'Cart · напиши 6-8 Test Cases по AC которые ты уже видел',
  brief:
    'Серж: «Cart surface · оформи 6-8 Test Cases · структура ID/Title/Preconditions/Steps/Expected/Postconditions. Покрывай happy + negative + edge. AC ты уже формализовал — теперь конвертим в executable TC». Маша: «QA-команда читает TC перед ручным regression · steps должны быть воспроизводимы любым новичком».',
  taskMd: [
    '**Контекст.** Cart surface уже готов · AC написаны. Test Case = AC превращённый в reproducible steps · любой QA может выполнить и подтвердить pass/fail. Без TC ручной regression становится «по памяти проверь как обычно».',
    '',
    '**Что делать.**',
    '1. Открой `/cart` в Atelier sandbox · поиграй: добавь/удали items · меняй quantity · перейди к оформлению · проверь empty state.',
    '2. В editor · напиши 6-8 Test Cases по template:',
    '   - **ID** (TC-CART-001 · monotonic)',
    '   - **Title** (короткий · что проверяем)',
    '   - **Preconditions** (что должно быть true до start)',
    '   - **Steps** (numbered 1·2·3 · atomic actions)',
    '   - **Expected** (наблюдаемое поведение после каждого step или в финале)',
    '   - **Postconditions** (state после успешного pass)',
    '3. Coverage:',
    '   - 2-3 happy path (display items · update qty · checkout)',
    '   - 2-3 negative (qty=0 · qty negative · remove last item)',
    '   - 1-2 edge (huge qty 999 · price overflow · одновременный clear+checkout)',
    '4. Жми **Save** · auto-grading проверит structure + coverage.',
    '',
    '**Что важно.**',
    '',
    '- **Steps атомарны** · «open cart» — ок · «open cart and check totals» — два step',
    '- **Expected конкретно** · «works» — bad · «subtotal = 10 400 ₽ · total = 10 400 ₽» — good',
    '- **Preconditions явно** · юзер залогинен? cart пуст? фикстура какая? — пиши',
    '- **ID соглашение** · TC-CART-NNN · monotonic · легко искать в test management tool',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: 'Total TC count в range 6-8 · balanced', weight: 1 },
      { id: 'r2', text: 'Каждый TC имеет ID/Title/Preconditions/Steps/Expected/Postconditions (или эквивалент)', weight: 2 },
      { id: 'r3', text: '≥2 happy path TC · positive flows покрыты', weight: 1 },
      { id: 'r4', text: '≥2 negative TC · invalid inputs / forbidden actions', weight: 2 },
      { id: 'r5', text: '≥1 edge TC · boundary / overflow / concurrent', weight: 1 },
      { id: 'r6', text: 'Steps атомарны · numbered · один step = одно действие', weight: 1 },
      { id: 'r7', text: 'Expected конкретно · measurable · не vague («works/correct»)', weight: 1 },
      { id: 'r8', text: 'Preconditions явные · фикстура / state описан', weight: 1 },
    ],
    passThreshold: 7,
  },
  sampleAnswer: [
    '## Test Cases · Cart surface',
    '',
    '### TC-CART-001 · Display items in cart',
    '- **Preconditions** · юзер залогинен · cart содержит 3 fixture items (Vapor #14 · Soft Loop · Grain Studies #3)',
    '- **Steps**',
    '  1. Открыть /cart',
    '- **Expected** · видны 3 items · каждый показывает title · artist · price (3 500 ₽ / 4 800 ₽ / 2 100 ₽) · subtotal = 10 400 ₽',
    '- **Postconditions** · cart state unchanged',
    '',
    '### TC-CART-002 · Increase quantity via «+» button',
    '- **Preconditions** · юзер на /cart · Vapor #14 quantity = 1',
    '- **Steps**',
    '  1. Нажать «+» рядом с Vapor #14',
    '- **Expected** · quantity field показывает 2 · subtotal обновился до 13 900 ₽ (1×7 000 + 4 800 + 2 100)',
    '- **Postconditions** · cart items.length = 3 · Vapor #14 quantity = 2',
    '',
    '### TC-CART-003 · Remove item via «×» button',
    '- **Preconditions** · юзер на /cart · 3 items',
    '- **Steps**',
    '  1. Нажать «×» рядом с Soft Loop',
    '- **Expected** · Soft Loop исчезает из списка · cart показывает 2 items · subtotal = 5 600 ₽',
    '- **Postconditions** · cart items.length = 2',
    '',
    '### TC-CART-004 · Quantity = 0 не должно проходить',
    '- **Preconditions** · юзер на /cart · Vapor #14 quantity = 1',
    '- **Steps**',
    '  1. Кликнуть в input количества',
    '  2. Стереть значение · ввести «0»',
    '  3. Tab из поля',
    '- **Expected** · quantity сбрасывается на 1 (минимум) · OR показывает inline error «количество ≥ 1»',
    '- **Postconditions** · cart items.length = 3 · Vapor #14 quantity = 1',
    '',
    '### TC-CART-005 · Negative quantity отклоняется',
    '- **Preconditions** · юзер на /cart',
    '- **Steps**',
    '  1. В input количества ввести «-5»',
    '  2. Tab из поля',
    '- **Expected** · отрицательное значение rejected · quantity = 1 (или предыдущее валидное) · subtotal не уходит в минус',
    '- **Postconditions** · cart subtotal > 0',
    '',
    '### TC-CART-006 · Empty cart показывает empty state',
    '- **Preconditions** · юзер на /cart · 3 items',
    '- **Steps**',
    '  1. Нажать «Очистить»',
    '- **Expected** · cart items.length = 0 · отображается empty illustration «Корзина пуста» + кнопка «Перейти в каталог»',
    '- **Postconditions** · cart пуст',
    '',
    '### TC-CART-007 · Edge · большое quantity (999) не ломает total',
    '- **Preconditions** · юзер на /cart · Vapor #14 priceRub = 3500',
    '- **Steps**',
    '  1. В input quantity ввести «999»',
    '  2. Tab',
    '- **Expected** · quantity = 999 принимается (либо capped к разумному maximum) · subtotal корректно отображается без overflow · форматирование «3 496 500 ₽» (или близкое)',
    '- **Postconditions** · cart functional · checkout button доступна',
    '',
    '### TC-CART-008 · Negative · checkout пустой корзины',
    '- **Preconditions** · юзер на /cart · cart пуст',
    '- **Steps**',
    '  1. (cart показывает empty state · кнопки «Оформить заказ» нет)',
    '- **Expected** · checkout CTA отсутствует в empty state · юзер видит «Перейти в каталог» вместо · регресс отсутствия protection не возможен',
    '- **Postconditions** · нет possibility to checkout zero items',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант** добавляет:',
    '',
    '1. **Test data строка** · фикстура с id/seed · легко regression voспроизводить',
    '2. **Automation candidate** mark · ⚙️ vs 🖐️ · QA-команда планирует sprint automation',
    '3. **Priority** · P1 happy path / P2 negative / P3 edge · если time-boxed regression — что cut сначала',
    '4. **Linked AC** · AC-NN reference · traceability AC → TC → bug',
    '',
    'Block 1 baseline = ID/Title/Pre/Steps/Expected/Post. Остальное добавляется по мере роста команды.',
  ].join('\n'),
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: ['author-junior', 'sprint-03-required', 'test-documentation'],
  estimateMin: 40,
  skills: { 'Test Documentation': 5, 'Requirements Analysis': 3 },
  requires: ['msw-api'],
};

// ────────────────────────────────────────────────────────────────────────────
// Sprint 03/04 · Bug Reports · B1-FOUND-03 · synergy с planted bug library
// ────────────────────────────────────────────────────────────────────────────

const B1_FOUND_03: Scenario = {
  id: 'B1-FOUND-03',
  sprint: 3,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/cart?sandbox-mode=on',
  mountStrategy: 'iframe-route',
  title: 'Sandbox bug hunt · напиши 3-4 Bug Reports по структуре',
  brief:
    'Серж: «Включён sandbox-mode · в Cart spотрел уже несколько багов. Напиши 3-4 Bug Reports · structure Summary/Steps/Expected/Actual/Severity/Environment. Юзай planted bug library как target list». Маша: «BR — это invoice от QA к dev. Чем чётче · тем быстрее fix · тем меньше back-and-forth».',
  taskMd: [
    '**Контекст.** Sandbox-mode активирован (URL `?sandbox-mode=on`) · Cart surface теперь содержит несколько deliberate bugs. Bug Report = artifact который dev читает и должен mock воспроизвести · без амбиции «не понятно что фиксить».',
    '',
    '**Что делать.**',
    '1. Открой `/cart?sandbox-mode=on` · поиграй: меняй quantity (особенно edges) · очищай cart · смотри totals.',
    '2. Найди ≥3 баги. Hints (если застрял):',
    '   - попробуй ввести negative quantity (-5) · что происходит с total?',
    '   - сравни subtotal vs total · cовпадают?',
    '   - очисти cart · посмотри empty state · normal?',
    '   - удали item (×) · UI обновился?',
    '3. На каждый bug · напиши Bug Report по template:',
    '   - **ID** (BR-NNN или auto)',
    '   - **Summary** (одна строка · WHAT/WHERE · напр. «Cart total accepts negative value при quantity = -1»)',
    '   - **Steps to Reproduce** (numbered · atomic · seed/preconditions)',
    '   - **Expected** (что должно происходить)',
    '   - **Actual** (что наблюдаем сейчас)',
    '   - **Severity** (Critical / Major / Minor с обоснованием)',
    '   - **Environment** (URL · browser · sandbox-mode state · timestamp)',
    '4. Save · auto-grading проверит structure · severity rationale · reproducibility.',
    '',
    '**Что важно.**',
    '',
    '- **Summary конкретно** · «cart broken» — bad · «Cart total accepts negative quantity → отрицательный subtotal» — good',
    '- **Steps воспроизводимы** · любой dev должен повторить · seed/state включи',
    '- **Severity обоснован** · Critical = блокирует release · Major = бьёт по бизнес-метрике · Minor = косметика',
    '- **Expected vs Actual** — два разных bullet · не сливай · dev должен видеть delta',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: '≥3 Bug Reports submitted · target из planted library', weight: 2 },
      { id: 'r2', text: 'Каждый BR имеет Summary/Steps/Expected/Actual/Severity/Environment', weight: 2 },
      { id: 'r3', text: 'Summary конкретно описывает WHAT · WHERE · trigger', weight: 1 },
      { id: 'r4', text: 'Steps воспроизводимы · atomic · numbered · seed/preconditions есть', weight: 2 },
      { id: 'r5', text: 'Expected ≠ Actual · обе строки заполнены · показывают delta', weight: 1 },
      { id: 'r6', text: 'Severity classified · с обоснованием (Critical/Major/Minor)', weight: 1 },
      { id: 'r7', text: 'Environment captured · URL · sandbox-mode flag · timestamp', weight: 1 },
    ],
    passThreshold: 7,
  },
  sampleAnswer: [
    '## Bug Reports · Cart sandbox-mode session',
    '',
    '### BR-001 · Negative quantity accepted · subtotal goes negative',
    '- **Summary** · Cart quantity input принимает negative value (-5) · subtotal становится отрицательным · total отражает invalid state',
    '- **Steps to Reproduce**',
    '  1. Открыть /cart?sandbox-mode=on',
    '  2. В quantity input для Vapor #14 ввести «-5»',
    '  3. Tab из поля',
    '- **Expected** · negative value rejected · quantity reverts к 1 (минимум) · inline error «количество ≥ 1»',
    '- **Actual** · quantity = -5 принят · Vapor subtotal = -17 500 ₽ · cart total = (-17 500 + 4 800 + 2 100) = -10 600 ₽',
    '- **Severity** · Major · invalid state не блокирует checkout · может привести к incorrect billing · но fix локален (input validation)',
    '- **Environment** · URL `/cart?sandbox-mode=on` · Chrome 140 · sandbox-mode=on (verified в badge) · 2026-05-11 14:32',
    '',
    '### BR-002 · Empty cart показывает «items: 0» вместо illustration',
    '- **Summary** · После очистки cart юзер видит plain text «items: 0» · нет иллюстрации · нет CTA «Перейти в каталог»',
    '- **Steps to Reproduce**',
    '  1. Открыть /cart?sandbox-mode=on с 3 fixture items',
    '  2. Нажать «Очистить»',
    '- **Expected** · empty state показывает иллюстрацию + текст «Корзина пуста» + кнопку «Перейти в каталог →»',
    '- **Actual** · отображается только «items: 0» plain text · no illustration · no CTA · юзер dead-ended',
    '- **Severity** · Major · UX regression блокирует return к каталогу · юзер вынужден manual navigate',
    '- **Environment** · `/cart?sandbox-mode=on` · Chrome 140 · 2026-05-11 14:35',
    '',
    '### BR-003 · Cart total отличается от subtotal без visible discount',
    '- **Summary** · Cart total показывает 81% of subtotal · нет visible coupon · нет explanation · «silent discount»',
    '- **Steps to Reproduce**',
    '  1. Открыть /cart?sandbox-mode=on с default fixture (3 items · subtotal = 10 400 ₽)',
    '  2. Смотреть summary block',
    '- **Expected** · total = subtotal (10 400 ₽) если нет применённого coupon · если есть — line «Скидка» с amount',
    '- **Actual** · subtotal = 10 400 ₽ · total = 8 424 ₽ (81% · 19% дешевле без объяснения)',
    '- **Severity** · Critical · revenue impact · юзер платит меньше чем должен · finance reconciliation сломан',
    '- **Environment** · `/cart?sandbox-mode=on` · Chrome 140 · 2026-05-11 14:38',
    '',
    '### BR-004 · Remove «×» button не убирает item из UI',
    '- **Summary** · Клик по «×» рядом с item не обновляет cart · item остаётся в списке · refresh страницы фиксит',
    '- **Steps to Reproduce**',
    '  1. Открыть /cart?sandbox-mode=on',
    '  2. Нажать «×» рядом с Soft Loop',
    '  3. Смотреть items list',
    '- **Expected** · Soft Loop исчезает immediately · cart items.length = 2 · subtotal обновился',
    '- **Actual** · Soft Loop остаётся · cart items.length = 3 (UI) · только F5 / route navigation фиксит state',
    '- **Severity** · Major · юзер думает что remove не работает · повторные клики · confusion',
    '- **Environment** · `/cart?sandbox-mode=on` · Chrome 140 · 2026-05-11 14:42',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант** добавляет к BR:',
    '',
    '1. **Stack/console logs** · network panel screenshots · request/response payload',
    '2. **Workaround** для юзера (если есть) · «refresh page» / «use alternative flow»',
    '3. **Suspected root cause** · «client cache invalidation likely missing» (полезно если знаешь codebase)',
    '4. **Regression scope** · «touched в commit abc123?» / «was this working last release?»',
    '5. **Linked AC/TC** · «violates AC-04» / «caught by TC-CART-005»',
    '',
    'Block 1 baseline = Summary/Steps/Expected/Actual/Severity/Env. Stack/logs — Block 2 territory (network инспекция).',
  ].join('\n'),
  scoring: { baseScore: 10, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: ['author-junior', 'sprint-03-required', 'bug-hunter', 'bug-report'],
  estimateMin: 45,
  skills: { 'Bug Reporting': 5, 'Exploratory Testing': 4 },
  requires: ['msw-api'],
};

// ────────────────────────────────────────────────────────────────────────────
// Sprint 05 · Test Design Techniques · B1-FOUND-04
// ────────────────────────────────────────────────────────────────────────────

const B1_FOUND_04: Scenario = {
  id: 'B1-FOUND-04',
  sprint: 5,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/place-order',
  mountStrategy: 'iframe-route',
  title: 'Place Order · применяй 4 техники test design',
  brief:
    'Серж: «Place Order сложнее Sign Up: 4 шага · форма · payment · review. Применяй 4 техники test design (EP · BVA · Decision Table · State Transition) — по 2-3 TC на каждую = 8-12 cases». Маша: «Без техник QA пишет «random» TC · с техниками — coverage measurable».',
  taskMd: [
    '**Контекст.** Чтобы coverage не была «как пришло в голову» · QA применяет formal techniques. 4 базовые техники из Sprint 05:',
    '',
    '- **Equivalence Partitioning (EP)** · разбей input domain на classes · 1 TC на class',
    '- **Boundary Value Analysis (BVA)** · границы classes — самое багогенное место',
    '- **Decision Table** · combinations conditions × actions → TC за каждую row',
    '- **State Transition** · UI state machine · TC за каждый legal/illegal transition',
    '',
    '**Что делать.**',
    '1. Открой `/place-order` · пройди flow (Address · Payment · Review · Confirmation). Заметь fields · conditions · transitions.',
    '2. Напиши 8-12 TC · по 2-3 на каждую технику:',
    '   - **EP** · postal code field · classes (digits 6 chars · digits wrong length · non-digit · empty)',
    '   - **BVA** · postal code length boundaries (5 · 6 · 7 chars)',
    '   - **Decision Table** · combinations (address valid · payment valid · review confirmed) × (proceed · block) — 4-8 rows',
    '   - **State Transition** · stepper transitions (step 1→2 · 2→3 · 3→4 · back · skip)',
    '3. Каждый TC — стандартная structure (ID/Title/Pre/Steps/Expected) + **Technique** tag · какая техника применена.',
    '',
    '**Что важно.**',
    '',
    '- **Технику явно tag-ай** · «(EP · class: valid digits)» · grader проверит',
    '- **Decision Table — табличка** · условия в header · TC = row · легко видеть combinations покрыты',
    '- **State Transition — диаграмма + TC** · хотя бы текстом описать states / transitions',
    '- **Boundaries — пара или triplet** · «5 · 6 · 7 chars» — boundary вокруг valid (6) · не один edge case',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: 'Total TC count в range 8-12 · balanced across techniques', weight: 1 },
      { id: 'r2', text: '≥2 TC применяют Equivalence Partitioning · classes явно названы', weight: 2 },
      { id: 'r3', text: '≥2 TC применяют BVA · boundaries pair/triplet вокруг valid value', weight: 2 },
      { id: 'r4', text: '≥2 TC из Decision Table · combinations conditions явно в табличной форме', weight: 2 },
      { id: 'r5', text: '≥2 TC из State Transition · transitions legal/illegal явно', weight: 2 },
      { id: 'r6', text: 'Каждый TC имеет Technique tag · grader может verify какая техника применена', weight: 1 },
      { id: 'r7', text: 'TC structure: ID/Title/Pre/Steps/Expected на каждом', weight: 1 },
    ],
    passThreshold: 8,
  },
  sampleAnswer: [
    '## Test Design · Place Order · 4 техники',
    '',
    '### EP · Postal code input',
    '',
    'Classes: (1) valid digits 6 chars · (2) digits wrong length · (3) non-digit chars · (4) empty.',
    '',
    '**TC-EP-01** · Valid postal code · class 1',
    '- **Pre** · юзер на step Address · все остальные fields валидны',
    '- **Steps** · ввести «119234» в postal code · нажать «Далее»',
    '- **Expected** · переход к step Payment · field подсветка success',
    '- **Technique** · EP · class: valid digits 6 chars',
    '',
    '**TC-EP-02** · Non-digit postal code · class 3',
    '- **Pre** · юзер на step Address',
    '- **Steps** · ввести «abcdef» в postal code · нажать «Далее»',
    '- **Expected** · inline error «postal code · только цифры» · форма НЕ submitted',
    '- **Technique** · EP · class: non-digit chars',
    '',
    '### BVA · Postal code length boundaries',
    '',
    'Valid length = 6. Boundaries: 5 (just below) · 6 (valid) · 7 (just above).',
    '',
    '**TC-BVA-01** · Length = 5 (just below)',
    '- **Pre** · address step · digits only',
    '- **Steps** · ввести «12345» · «Далее»',
    '- **Expected** · error «postal code · 6 цифр» · submit blocked',
    '- **Technique** · BVA · boundary -1',
    '',
    '**TC-BVA-02** · Length = 6 (valid)',
    '- **Steps** · «123456» · «Далее»',
    '- **Expected** · accepted · transition step 1→2',
    '- **Technique** · BVA · boundary on',
    '',
    '**TC-BVA-03** · Length = 7 (just above)',
    '- **Steps** · «1234567» · «Далее»',
    '- **Expected** · error · submit blocked',
    '- **Technique** · BVA · boundary +1',
    '',
    '### Decision Table · proceed-to-payment conditions',
    '',
    '| TC | Address valid | Card valid | Terms accepted | → Action |',
    '| -- | ------------- | ---------- | -------------- | -------- |',
    '| DT-01 | ✓ | ✓ | ✓ | proceed |',
    '| DT-02 | ✓ | ✓ | ✗ | block · «accept terms» |',
    '| DT-03 | ✓ | ✗ | ✓ | block · «invalid card» |',
    '| DT-04 | ✗ | ✓ | ✓ | block · «invalid address» |',
    '',
    '**TC-DT-01** · all valid → proceed',
    '- **Steps** · заполнить все fields валидно · «Оформить»',
    '- **Expected** · переход к confirmation · order placed',
    '- **Technique** · Decision Table · row 1',
    '',
    '**TC-DT-02** · terms unchecked → block',
    '- **Steps** · все fields valid · NOT check terms · «Оформить»',
    '- **Expected** · error «согласитесь с условиями» · order NOT placed',
    '- **Technique** · Decision Table · row 2',
    '',
    '### State Transition · stepper',
    '',
    'States: S1 Address · S2 Payment · S3 Review · S4 Confirmation. Legal: S1→S2→S3→S4 · S2→S1 · S3→S2. Illegal: S1→S3 (skip Payment).',
    '',
    '**TC-ST-01** · Forward S1→S2 (legal)',
    '- **Steps** · address заполнен · «Далее»',
    '- **Expected** · UI показывает Payment step · stepper highlights step 2',
    '- **Technique** · State Transition · legal forward',
    '',
    '**TC-ST-02** · Backward S2→S1 (legal)',
    '- **Pre** · юзер на step 2 · ввёл частично card data',
    '- **Steps** · нажать «Назад»',
    '- **Expected** · возврат к Address · введённые address fields сохранены',
    '- **Technique** · State Transition · legal backward · с state preservation',
    '',
    '**TC-ST-03** · Skip S1→S3 (illegal)',
    '- **Steps** · попытка direct URL navigation `/place-order?step=3` без заполнения address/payment',
    '- **Expected** · redirect к step 1 · state не leaked',
    '- **Technique** · State Transition · illegal · защита от deeplink skip',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант** добавляет:',
    '',
    '1. **Pairwise (orthogonal arrays)** · combinations factor reduction · если 4+ conditions',
    '2. **Cause-Effect Graph** · alt visualization decision table для complex conditions',
    '3. **Risk-Based Test Prioritization** · weight TC по business impact',
    '4. **Combinatorial Coverage Metrics** · 2-way / 3-way coverage formalization',
    '',
    'Block 1 baseline = 4 техники (EP/BVA/DT/ST). Pairwise — Block 3 territory (Test Design Patterns deep).',
  ].join('\n'),
  scoring: { baseScore: 11, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: ['author-junior', 'sprint-05-required', 'test-design'],
  estimateMin: 55,
  skills: { 'Test Design': 6, 'Test Documentation': 4 },
  requires: ['msw-api'],
};

// ────────────────────────────────────────────────────────────────────────────
// Sprint 04 · Full Feature Documentation · B1-FOUND-05 · Block 1 capstone-like
// ────────────────────────────────────────────────────────────────────────────

const B1_FOUND_05: Scenario = {
  id: 'B1-FOUND-05',
  sprint: 4,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/profile',
  mountStrategy: 'iframe-route',
  title: 'Profile feature · full документация · capstone Block 1',
  brief:
    'Серж: «Capstone Block 1 · Profile feature · оформи всё: User Story · AC · TC · 1-2 BR (если найдёшь) · test data · summary risks. Это шаблон handoff dev → QA → release». Маша: «Доку прочтёт product · dev · CFO. Если читатель не QA — он должен понять что важно и что покрыто».',
  taskMd: [
    '**Контекст.** Полный package документации = единственный артефакт который QA сдаёт за feature. Внутри:',
    '',
    '- User Story (1) — feature framing',
    '- AC (5-7) — boundary правила',
    '- TC (6-8) — executable coverage',
    '- BR (1-2 если найдены) — recorded defects',
    '- Test Data (короткий блок) — фикстуры/seeds',
    '- Risks Summary (3-5 bullets) — что НЕ покрыто · известные ограничения',
    '',
    '**Что делать.**',
    '1. Открой `/profile` в Atelier · познакомься: view mode · edit mode · order history.',
    '2. В editor собери full doc по 6 sections:',
    '   - **§1 User Story**',
    '   - **§2 AC** (5-7 · 4 категории coverage)',
    '   - **§3 TC** (6-8 · happy/negative/edge · с technique tags если применимо)',
    '   - **§4 BR** (1-2 · если sandbox-mode ON · найди и оформи)',
    '   - **§5 Test Data** (фикстура · seed · default user)',
    '   - **§6 Risks** (3-5 bullets · что вне scope · known gaps)',
    '3. Save · auto-grading проверит все 6 sections + structure внутри каждой.',
    '',
    '**Что важно.**',
    '',
    '- **6 sections все present** · skip одного = ≤6 на rubric',
    '- **Cross-section links** · AC-03 referenced в TC-04 · BR-01 violates AC-02 · трассируемость',
    '- **Risks honest** · «edit без admin-role не tested» — ок · «всё покрыто» — bad sign',
    '- **Test data явно** · «default user: anya@art.studio · profile: name «Анна Иванова» · 2 orders» · dev может seed',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: '§1 User Story в формате As-I-So', weight: 1 },
      { id: 'r2', text: '§2 AC: 5-7 items · 4 категории coverage', weight: 2 },
      { id: 'r3', text: '§3 TC: 6-8 items · happy/negative/edge balanced · structure complete', weight: 2 },
      { id: 'r4', text: '§4 BR: ≥1 bug report · structure complete · severity classified', weight: 2 },
      { id: 'r5', text: '§5 Test Data: фикстура / seed / default user явно описаны', weight: 1 },
      { id: 'r6', text: '§6 Risks: 3-5 bullets · known gaps · out-of-scope items', weight: 2 },
      { id: 'r7', text: 'Cross-section links · AC referenced в TC · BR violations указаны', weight: 1 },
      { id: 'r8', text: 'Document holistically readable · даже non-QA reader понимает scope', weight: 1 },
    ],
    passThreshold: 8,
  },
  sampleAnswer: [
    '# Profile feature · QA documentation package',
    '',
    '## §1 User Story',
    '',
    'As a registered Atelier user · I want к view и edit my profile (name · email · display name) и review my order history · So that I keep my account current и track my purchases.',
    '',
    '## §2 Acceptance Criteria',
    '',
    '- **AC-01 · Happy** · View mode displays name · email · display name · join date · order count · все fields из profile fixture',
    '- **AC-02 · Negative** · Edit form с invalid email format показывает inline error · save blocked',
    '- **AC-03 · Negative** · Empty display name на save shows error «display name обязателен» · form NOT submitted',
    '- **AC-04 · Edge** · Display name с unicode (русские буквы · emoji) accepted',
    '- **AC-05 · Edge** · Order history с 0 orders показывает empty state «заказов пока нет»',
    '- **AC-06 · Security** · Edit form session-protected · juste logged-out юзер redirected к /sign-up',
    '- **AC-07 · Security** · Email change requires confirmation flow (email link verification) · НЕ instant write',
    '',
    '## §3 Test Cases',
    '',
    '### TC-PROF-01 · View profile (happy)',
    '- **Pre** · юзер залогинен · profile fixture loaded · 2 orders в history',
    '- **Steps** · 1. Открыть /profile',
    '- **Expected** · видим name «Анна Иванова» · email «anya@art.studio» · display name «anya_petrova» · join date · order count = 2',
    '- **Linked** · AC-01',
    '',
    '### TC-PROF-02 · Edit display name (happy)',
    '- **Pre** · юзер на /profile · view mode',
    '- **Steps** · 1. Нажать «Редактировать» · 2. Изменить display name на «anya_2026» · 3. Save',
    '- **Expected** · success message · view mode показывает «anya_2026» · navigate fixes if stale',
    '- **Linked** · AC-04',
    '',
    '### TC-PROF-03 · Invalid email (negative)',
    '- **Pre** · юзер в edit mode',
    '- **Steps** · 1. Стереть email · ввести «notanemail» · 2. Save',
    '- **Expected** · inline error «корректный email» · save blocked',
    '- **Linked** · AC-02',
    '',
    '### TC-PROF-04 · Empty display name (negative)',
    '- **Pre** · edit mode',
    '- **Steps** · 1. Стереть display name · 2. Save',
    '- **Expected** · error «display name обязателен» · save blocked',
    '- **Linked** · AC-03',
    '',
    '### TC-PROF-05 · Order history с 2 orders (happy)',
    '- **Pre** · юзер с 2 заказами',
    '- **Steps** · 1. Открыть /profile · 2. Прокрутить к Order history section',
    '- **Expected** · 2 cards · даты · статусы · totals',
    '',
    '### TC-PROF-06 · Empty order history (edge)',
    '- **Pre** · new user · 0 orders',
    '- **Steps** · 1. /profile',
    '- **Expected** · empty state «заказов пока нет» · CTA «Перейти в каталог»',
    '- **Linked** · AC-05',
    '',
    '### TC-PROF-07 · Edit без auth (security)',
    '- **Pre** · юзер logged out',
    '- **Steps** · 1. Direct URL /profile?edit=1',
    '- **Expected** · redirect к /sign-up · session check enforced',
    '- **Linked** · AC-06',
    '',
    '## §4 Bug Reports (sandbox-mode on)',
    '',
    '### BR-PROF-01 · Display name stale after save · navigate fixes',
    '- **Summary** · После save display name UI показывает старое значение · navigate refresh показывает новое',
    '- **Steps** · 1. Edit mode · 2. Change display name на «test_new» · 3. Save · 4. Смотреть view mode',
    '- **Expected** · view mode показывает «test_new» immediately',
    '- **Actual** · показывается старое значение «anya_petrova» · только route navigation фиксит',
    '- **Severity** · Major · юзер думает что save не сработал · повторные tries · confusion',
    '- **Environment** · /profile?sandbox-mode=on · 2026-05-11',
    '- **Violates** · AC-04 (display name updated после save)',
    '',
    '## §5 Test Data',
    '',
    '- **Default user fixture** · email `anya@art.studio` · username `anya_petrova` · display name `Анна Иванова` · join date 2025-12-01 · role `customer`',
    '- **Orders fixture** · 2 orders · order_001 (2026-04-15 · Vapor #14 · 3 500 ₽ · status «delivered») · order_002 (2026-05-02 · Soft Loop · 4 800 ₽ · status «shipped»)',
    '- **Empty-state user** · email `new@art.studio` · 0 orders · для TC-PROF-06',
    '- **Seed command** · `localStorage.setItem(\'qastudio.v1.profile-fixture\', \'default\')`',
    '',
    '## §6 Risks · что НЕ покрыто',
    '',
    '- **Admin/Editor role permissions** · viewer role behavior проверен · admin/editor не testedscope · planned Block 2',
    '- **Profile photo upload** · feature не в текущем scope · если включат — separate TC pack',
    '- **Email change confirmation flow** · AC-07 declared но end-to-end (получение email · клик link · session refresh) requires backend integration · Block 3 territory',
    '- **Concurrent edit** · 2 tabs одновременный save — undefined behavior · documented как known gap',
    '- **Localization** · ru-RU only tested · en/tr копии не verified',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант** добавляет к 6 sections:',
    '',
    '1. **§7 Performance budget** · profile load <2s · edit save <500ms p95',
    '2. **§8 Accessibility checklist** · WCAG 2.1 AA verified · keyboard nav · screen reader labels',
    '3. **§9 Telemetry hooks** · какие events emitted (profile_viewed · profile_edited · save_failed) · для product analytics',
    '4. **§10 Rollback plan** · если release вылет · какие steps cancel feature flag',
    '',
    'Block 1 baseline = 6 sections (US/AC/TC/BR/Data/Risks). §7-10 добавляются по мере senior maturity.',
  ].join('\n'),
  scoring: { baseScore: 12, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: ['author-junior', 'sprint-04-required', 'documentation', 'capstone-block-1'],
  estimateMin: 75,
  skills: { 'Test Documentation': 7, 'Requirements Analysis': 5, 'Bug Reporting': 3 },
  requires: ['msw-api'],
};

// ────────────────────────────────────────────────────────────────────────────
// BLOCK_1_FOUND_SCENARIOS export · concatenated by sprint
// ────────────────────────────────────────────────────────────────────────────

export const BLOCK_1_FOUND_SCENARIOS: ReadonlyArray<Scenario> = [
  // S02 · Acceptance Criteria · B1-FOUND-01 (W5.2 batch 1 · midpoint review)
  B1_FOUND_01,
  // S03 · Test Cases · B1-FOUND-02 (batch 2)
  B1_FOUND_02,
  // S03/S04 · Bug Reports · B1-FOUND-03 (batch 2 · sandbox-mode synergy)
  B1_FOUND_03,
  // S05 · Test Design Techniques · B1-FOUND-04 (batch 2 · EP/BVA/DT/ST)
  B1_FOUND_04,
  // S04 · Full Feature Documentation · B1-FOUND-05 (batch 2 · Block 1 capstone-like)
  B1_FOUND_05,
];
